import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { ADVANCED_METRICS } from '@/constants/metrics';

interface AdvancedMetricsConfigProps {
  enabled: boolean;
  mode: 'predefined' | 'customizable';
  maxMetrics: number;
  selectedMetrics: string[];
  onEnabledChange: (enabled: boolean) => void;
  onModeChange: (mode: 'predefined' | 'customizable') => void;
  onMaxMetricsChange: (max: number) => void;
  onSelectedMetricsChange: (metrics: string[]) => void;
}

export function AdvancedMetricsConfig({
  enabled,
  mode,
  maxMetrics,
  selectedMetrics,
  onEnabledChange,
  onModeChange,
  onMaxMetricsChange,
  onSelectedMetricsChange,
}: AdvancedMetricsConfigProps) {
  const handleToggle = (metric: string) => {
    if (selectedMetrics.includes(metric)) {
      onSelectedMetricsChange(selectedMetrics.filter((m) => m !== metric));
    } else {
      onSelectedMetricsChange([...selectedMetrics, metric]);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label htmlFor="advanced-metrics-enabled">Métricas Avançadas</Label>
        <Switch
          id="advanced-metrics-enabled"
          checked={enabled}
          onCheckedChange={onEnabledChange}
        />
      </div>

      {enabled && (
        <>
          <RadioGroup value={mode} onValueChange={(v) => onModeChange(v as 'predefined' | 'customizable')}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="predefined" id="metrics-predefined" />
              <label htmlFor="metrics-predefined" className="text-sm cursor-pointer">
                Pré-definido (SuperAdmin escolhe quais métricas)
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="customizable" id="metrics-customizable" />
              <label htmlFor="metrics-customizable" className="text-sm cursor-pointer">
                Customizável (Admin escolhe dentro de um limite)
              </label>
            </div>
          </RadioGroup>

          {mode === 'predefined' ? (
            <div className="space-y-3 p-4 border rounded-lg bg-muted/50">
              <Label>Métricas Avançadas Incluídas</Label>
              <div className="grid grid-cols-2 gap-3">
                {ADVANCED_METRICS.map((metric) => (
                  <div key={metric} className="flex items-center space-x-2">
                    <Checkbox
                      id={`adv-metric-${metric}`}
                      checked={selectedMetrics.includes(metric)}
                      onCheckedChange={() => handleToggle(metric)}
                    />
                    <label
                      htmlFor={`adv-metric-${metric}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {metric}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-2 p-4 border rounded-lg bg-muted/50">
              <Label htmlFor="max-advanced-metrics">Máximo de Métricas Avançadas</Label>
              <Input
                id="max-advanced-metrics"
                type="number"
                min="0"
                max={ADVANCED_METRICS.length}
                value={maxMetrics}
                onChange={(e) => onMaxMetricsChange(parseInt(e.target.value) || 0)}
              />
              <p className="text-xs text-muted-foreground">
                O Admin do tenant poderá escolher até {maxMetrics} métrica(s) avançada(s)
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
